<template>
  <q-card flat bordered>
    <q-card-section class="q-pb-sm row items-center justify-between">
      <h6>Preview</h6>
      <q-btn-toggle
        v-model="zoomMode"
        dense
        no-caps
        flat
        toggle-color="primary"
        color="subtle"
        :options="[
          { label: 'Fit', value: 'fit' },
          { label: '100%', value: 'full' }
        ]"
      />
    </q-card-section>

    <q-card-section class="q-pt-none">
      <!-- ---------- stage ---------- -->
      <div
        ref="stage"
        class="preview-stage"
        :class="{ '--scroll': zoomMode === 'full' }"
      >
        <div
          class="preview-canvas"
          :style="{
            width: width * scale + 'px',
            height: height * scale + 'px'
          }"
        >
          <div
            class="preview-canvas-inner"
            :style="{
              width: width + 'px',
              height: height + 'px',
              transform: 'scale(' + scale + ')'
            }"
          >
            <!-- Browser-rendered HTML in a sandboxed iframe — effectively the
                 same engine as the headless Chromium render Lambda. -->
            <iframe
              class="preview-frame"
              :srcdoc="frameDoc"
              sandbox="allow-same-origin"
              :style="{ width: width + 'px', height: height + 'px' }"
              title="Template preview"
            ></iframe>
          </div>
        </div>
      </div>

      <!-- ---------- telemetry ---------- -->
      <div class="preview-telemetry">
        <!--<span class="--engine">CHROMIUM</span>-->
        <span>Live Preview</span>
        <span>{{ width }} × {{ height }} px</span>
        <span>{{ Math.round(scale * 100) }}%</span>
      </div>
    </q-card-section>
  </q-card>
</template>

<script>
export default {
  name: "TemplateEditorPreviewPane",

  props: {
    html: { type: String, required: true },
    width: { type: Number, required: true },
    height: { type: Number, required: true }
  },

  data() {
    return {
      zoomMode: "fit",
      scale: 1,
      resizeObserver: null
    };
  },

  computed: {
    /** Full document for the iframe preview, sized exactly to the canvas. */
    frameDoc() {
      return `<!doctype html><html><head><meta charset="utf-8">
<style>
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');
  * { box-sizing: border-box; margin: 0; }
  html, body { width: ${this.width}px; height: ${this.height}px; overflow: hidden; }
</style></head>
<body>${this.html}</body></html>`;
    }
  },

  watch: {
    width() {
      this.fitScale();
    },
    height() {
      this.fitScale();
    },
    zoomMode() {
      this.fitScale();
    }
  },

  mounted() {
    this.resizeObserver = new ResizeObserver(() => this.fitScale());
    this.resizeObserver.observe(this.$refs.stage);
    this.fitScale();
  },

  beforeUnmount() {
    if (this.resizeObserver) this.resizeObserver.disconnect();
  },

  methods: {
    fitScale() {
      if (this.zoomMode === "full") {
        this.scale = 1;
        return;
      }
      const stage = this.$refs.stage;
      if (!stage) return;
      const pad = 48;
      const availW = stage.clientWidth - pad;
      const availH = stage.clientHeight - pad;
      this.scale = Math.min(1, availW / this.width, availH / this.height);
    }
  }
};
</script>

<style scoped lang="scss">
.preview-stage {
  position: relative;
  height: 520px;
  border: 1px solid color(border);
  border-radius: 4px;
  background-color: color(gray, light);
  background-image: radial-gradient(rgba(0, 0, 0, 0.08) 1px, transparent 1px);
  background-size: 22px 22px;
  @include display-flex(row, center, center);
  overflow: hidden;

  &.--scroll {
    overflow: auto;
    align-items: flex-start;
    justify-content: flex-start;
    padding: 24px;
  }
}

.preview-canvas {
  position: relative;
  flex: none;
  border-radius: 2px;
  box-shadow: 0 4px 18px rgba(0, 0, 0, 0.15);
}

.preview-canvas-inner {
  transform-origin: top left;
  overflow: hidden;
}

.preview-frame {
  border: none;
  display: block;
  background: color(background, light);
}

.preview-telemetry {
  @include display-flex(row, flex-start, center);
  gap: 14px;
  padding-top: 12px;
  color: color(gray, medium-dark);
  font-size: 12px;

  .--engine {
    font-weight: 700;
    color: color(font);
  }
}
</style>
